import classNames from 'classnames';
import { memo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FilterAccordion } from '@sorare/core/src/atoms/layout/Accordion';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS, Text16 } from '@sorare/core/src/atoms/typography';
import useLocalStorageToggle from '@sorare/core/src/hooks/useLocalStorageToggle';
import {
  FilterSeparator,
  FilterWidget,
  FiltersGroup as FiltersGroupType,
} from '@sorare/core/src/lib/filters';

import ClearAllFilters from '../ClearAllFilters';

export type Props = {
  filters: FiltersGroupType[];
  advancedFilters?: FiltersGroupType[];
  skipClearAllButton?: boolean;
};

const Root = styled(Vertical).attrs({ gap: 0 })``;
const FilterRoot = styled.div`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const FiltersGroup = styled.div`
  margin-bottom: var(--double-unit);
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const FiltersGroupTitle = styled(LabelS)`
  margin-bottom: var(--half-unit);
`;

const FiltersGroupWrapper = styled.div`
  overflow: hidden;
  border-radius: var(--unit);
  box-shadow: inset 0px 0px 0px 1px var(--c-neutral-150);
`;

const Separator = styled.div`
  border-top: 1px solid var(--c-neutral-200);
`;
const Footer = styled.div`
  &.withBorder {
    border-top: 1px solid var(--c-neutral-200);
  }
  padding: var(--double-unit) 0;
`;

const messages = defineMessages({
  showMore: {
    id: 'FiltersManager.showMoreFilters',
    defaultMessage: 'Show more filters',
  },
  showLess: {
    id: 'FiltersManager.showLessFilters',
    defaultMessage: 'Show less filters',
  },
});

const Filter = ({
  filter,
  noBorder,
}: {
  filter: FilterWidget | FilterSeparator;
  noBorder?: boolean;
}) => {
  if (filter.type === 'separator') {
    return <Separator />;
  }
  return (
    <FilterRoot key={filter.key}>
      <FilterAccordion
        title={filter.title}
        startsOpen={filter.accordionOptions?.startsOpen}
        noBorder={noBorder}
      >
        {({ expanded }) => filter.Component({ visible: expanded })}
      </FilterAccordion>
    </FilterRoot>
  );
};

export const Filters = ({ filters }: { filters: Props['filters'] }) => {
  return (
    <>
      {filters.map(filtersGroup => {
        return (
          <FiltersGroup key={filtersGroup.slug}>
            {filtersGroup.title && (
              <FiltersGroupTitle uppercase bold color="var(--c-neutral-600)">
                {filtersGroup.title}
              </FiltersGroupTitle>
            )}
            {filtersGroup.filter ? (
              filtersGroup.filter.Component({ visible: true })
            ) : (
              <FiltersGroupWrapper>
                {filtersGroup.filters.map((filter, ii) => {
                  const key =
                    filter.type === 'separator'
                      ? `separator-${ii}`
                      : filter.key;
                  return <Filter key={key} filter={filter} noBorder />;
                })}
              </FiltersGroupWrapper>
            )}
          </FiltersGroup>
        );
      })}
    </>
  );
};

export const FiltersManager = ({
  filters,
  advancedFilters,
  skipClearAllButton,
}: Props) => {
  const [showAdvancedFilters, toggleShowAdvancedFilters] =
    useLocalStorageToggle('SHOW_ADVANCED_FILTERS', false);

  const [lastFilter] = filters.flatMap(filter => filter.filters).slice(-1);
  const flatAdvancedFilters = advancedFilters?.flatMap(
    filter => filter.filters
  );
  const lastAdvancedFilter = flatAdvancedFilters
    ? flatAdvancedFilters.slice(-1)[0]
    : null;

  const lastItemIsSeparator =
    ((!showAdvancedFilters || !flatAdvancedFilters) &&
      lastFilter.type === 'separator') ||
    (showAdvancedFilters &&
      lastAdvancedFilter &&
      lastAdvancedFilter.type === 'separator');

  return (
    <Root>
      <div>
        <Filters filters={filters} />
        {advancedFilters && (
          <Collapse open={showAdvancedFilters} hideGap>
            <Filters filters={advancedFilters} />
          </Collapse>
        )}
      </div>
      {(!!advancedFilters || !skipClearAllButton) && (
        <Footer className={classNames({ withBorder: !lastItemIsSeparator })}>
          {advancedFilters && (
            <button type="button" onClick={toggleShowAdvancedFilters}>
              <Text16 color="var(--c-brand-300)">
                <FormattedMessage
                  {...(showAdvancedFilters
                    ? messages.showLess
                    : messages.showMore)}
                />
              </Text16>
            </button>
          )}
          {!skipClearAllButton && <ClearAllFilters />}
        </Footer>
      )}
    </Root>
  );
};

export default memo(FiltersManager);
